import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import { Typography, useMediaQuery, useTheme } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import {
  Banknote,
  Calendar,
  ChartLine,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ClipboardClock,
  Footprints,
  Inbox,
  Megaphone,
  Rocket,
  Store,
  Users,
  UserStar,
} from 'lucide-react';
import NavLink from './NavLink';
import { LoadedUser } from '../../interfaces';
import { ROUTES } from '../../constants/global';
import useGetUnreadRequests from '../../hooks/requests/useGetUnreadRequests';
import useBusiness from '../../hooks/businesses/useBusiness';
import { isTeamManager, isUserAdminOrManager } from '../../utils/global/helper';
import { Feature } from '../../types/subscriptions';
import useFeatureGuard from '../../hooks/subscriptions/useFeatureGuard';
import useUser from '../../hooks/global/useUser';
import { userHasAccessToQueue } from '../../helpers/queue';
import { hasAdsAccess } from '../../helpers/ads';
import { canIndependentManageShop } from '../../helpers/businesses';
import useGetOnboarding from '../../hooks/businesses/useGetOnboarding';
import useGetJoinRequests from '../../hooks/joinRequests/useGetJoinRequests';
import useBanners from '../../hooks/navigation/useBanners';
import { BANNER_HEIGHT } from '../global/Banner';
import './navigation.css';

const SETUP_EXPANDED_KEY = 'shopSetupExpanded';

interface NavLinkItem {
  name: string;
  route: string;
  icon: React.ComponentProps<typeof NavLink>['icon'];
  visible: boolean;
  badgeCount?: number;
}

interface SetupChildItem {
  name: string;
  route: string;
  visible: boolean;
  showDot?: boolean;
}

interface Props {
  mobileDrawerOpen: boolean;
  setMobileDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  loadedUser: LoadedUser | undefined;
}

function NavLinks({
  links,
  path,
  onNavigate,
}: {
  links: NavLinkItem[];
  path: string;
  onNavigate?: () => void;
}) {
  return (
    <>
      {links.map(link => (
        <NavLink
          key={link.name}
          linkName={link.name}
          linkRoute={link.route}
          isActive={!!link.route && path.includes(link.route)}
          icon={link.icon}
          badgeCount={link.badgeCount}
          onNavigate={onNavigate}
        />
      ))}
    </>
  );
}

function SideNav({ mobileDrawerOpen, setMobileDrawerOpen, loadedUser }: Props) {
  const theme = useTheme();
  const { user } = useUser();
  const { isIndependent } = useBusiness();
  const canManageMarketing = useFeatureGuard(Feature.MARKETING);
  const canAccessAds = useFeatureGuard(Feature.ADS);
  const hasItemLibrary = useFeatureGuard(Feature.ITEM_LIBRARY);

  const isDesktop = useMediaQuery('(min-width:900px)');
  const path = useLocation().pathname;
  const { isBannerShown } = useBanners();

  const {
    isEnabled: isOnboardingEnabled,
    isOnboardingComplete,
    isLoading: isOnboardingLoading,
    steps,
    completedSteps,
    percentCompleted,
  } = useGetOnboarding();
  const showSetupRow =
    isOnboardingEnabled && !isOnboardingLoading && !isOnboardingComplete;

  const { data: joinRequests } = useGetJoinRequests();
  const hasPendingJoinRequests =
    !!loadedUser && isTeamManager(loadedUser) && !!joinRequests?.length;

  const { data: unreadRequestsMap } = useGetUnreadRequests({
    listen: true,
  });
  const unreadRequests = [
    ...(unreadRequestsMap?.appointments ?? []),
    ...(unreadRequestsMap?.generalRequests ?? []),
  ];

  const topLinks: NavLinkItem[] = [
    {
      name: 'Calendar',
      route: ROUTES.calendar,
      icon: Calendar,
      visible: true,
    },
    {
      name: 'Booking Requests',
      route: ROUTES.requests,
      icon: Inbox,
      visible: true,
      badgeCount: unreadRequests?.length || 0,
    },
    {
      name: 'Walk-in Queue',
      route: ROUTES.queue,
      icon: Footprints,
      visible: !!user && userHasAccessToQueue(user),
    },
    {
      name: 'Waitlist',
      route: ROUTES.cancellationWaitlist,
      icon: ClipboardClock,
      visible: true,
    },
  ];

  const bottomLinks: NavLinkItem[] = [
    {
      name: 'Customers',
      route: ROUTES.customers,
      icon: Users,
      visible: true,
    },
    {
      name: 'Checkout',
      route: ROUTES.checkouts,
      icon: Banknote,
      visible: true,
    },
    {
      name: 'Marketing',
      route: ROUTES.marketing,
      icon: Megaphone,
      visible: !!loadedUser,
    },
    {
      name: 'Online Reviews',
      route: ROUTES.reviews,
      icon: UserStar,
      visible:
        !!loadedUser &&
        canManageMarketing &&
        (isIndependent ||
          (isUserAdminOrManager(loadedUser) && loadedUser.canManageCampaigns)),
    },
    {
      name: 'Ads',
      route: ROUTES.ads,
      icon: Rocket,
      visible: hasAdsAccess(loadedUser, canAccessAds),
    },
    {
      name: 'Reports',
      route: ROUTES.reports,
      icon: ChartLine,
      visible: !!loadedUser,
    },
  ];

  const isIndependentBusiness = loadedUser?.business.type === 'INDEPENDENT';
  const isManagerOrIndependent =
    !!loadedUser && (isUserAdminOrManager(loadedUser) || isIndependentBusiness);
  const isUserTeamManager =
    !!loadedUser &&
    (isIndependentBusiness ||
      (loadedUser.role === 'SHOP_MANAGER' && loadedUser.canManageTeam));
  const canManageShop =
    !!loadedUser &&
    (isUserAdminOrManager(loadedUser) || canIndependentManageShop(loadedUser));
  const isStudioShopManager =
    !!loadedUser &&
    loadedUser.business.type === 'STUDIO' &&
    isUserAdminOrManager(loadedUser);

  const setupChildren: SetupChildItem[] = [
    {
      name: 'Manage Shop',
      route: ROUTES.shop,
      visible: canManageShop,
    },
    {
      name: 'Manage Team',
      route: ROUTES.team,
      visible: isUserTeamManager,
      showDot: hasPendingJoinRequests,
    },
    {
      name: hasItemLibrary ? 'Items & Gift Cards' : 'Gift Cards',
      route: ROUTES.items,
      visible: canManageShop,
    },
    {
      name: 'Waivers',
      route: ROUTES.waivers,
      visible: true,
    },
    {
      name: 'Booking Site',
      route: ROUTES.bookingSite,
      visible: isStudioShopManager,
    },
    {
      name: 'Flashbook',
      route: ROUTES.flashbook,
      visible: isStudioShopManager,
    },
    {
      name: 'Booking Links',
      route: ROUTES.bookingLinks,
      visible: isManagerOrIndependent,
    },
    {
      name: 'Import Data',
      route: ROUTES.import,
      visible: isStudioShopManager,
    },
  ];

  const visibleTopLinks = topLinks.filter(link => link.visible);
  const visibleBottomLinks = bottomLinks.filter(link => link.visible);
  const visibleSetupChildren = setupChildren.filter(child => child.visible);

  const [setupExpanded, setSetupExpanded] = React.useState(
    () => sessionStorage.getItem(SETUP_EXPANDED_KEY) === 'true',
  );
  const toggleSetup = () =>
    setSetupExpanded(prev => {
      sessionStorage.setItem(SETUP_EXPANDED_KEY, String(!prev));
      return !prev;
    });

  const isSetupChildActive = visibleSetupChildren.some(child =>
    path.includes(child.route),
  );
  React.useEffect(() => {
    if (isSetupChildActive) setSetupExpanded(true);
  }, [isSetupChildActive]);

  const showParentDot = !setupExpanded && hasPendingJoinRequests;
  const SetupChevron = setupExpanded ? ChevronUp : ChevronDown;

  const drawerWidth = isDesktop ? 248 : '100%';
  const closeMobileDrawer = () => setMobileDrawerOpen(false);

  const renderContent = (onNavigate?: () => void) => (
    <Box
      sx={{
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
      }}>
      <Box>
        {showSetupRow && (
          <Link
            to={ROUTES.getStarted}
            className="setup-progress-row"
            onClick={onNavigate}>
            <span
              className="setup-progress-ring"
              style={{
                background: `conic-gradient(${theme.palette.primary.main} 0 ${percentCompleted}%, #E4E4E4 ${percentCompleted}% 100%)`,
              }}>
              <span />
            </span>
            <span className="setup-progress-label">Get set up</span>
            <span className="setup-progress-count">
              {completedSteps.length}/{steps.length}
            </span>
            <ChevronRight size={18} strokeWidth={1.75} color="#9A9A9A" />
          </Link>
        )}
        <List className="nav-links">
          <NavLinks
            links={visibleTopLinks}
            path={path}
            onNavigate={onNavigate}
          />
          {!!visibleTopLinks.length && !!visibleBottomLinks.length && (
            <Divider component="li" className="nav-zone-divider" />
          )}
          <NavLinks
            links={visibleBottomLinks}
            path={path}
            onNavigate={onNavigate}
          />
        </List>
      </Box>
      {!!visibleSetupChildren.length && (
        <Box className="shop-setup-zone">
          <Divider />
          <ButtonBase
            disableRipple
            onClick={toggleSetup}
            className="shop-setup-parent">
            <Store
              size={20}
              strokeWidth={1.75}
              color="#6E6E6E"
              style={{ flexShrink: 0 }}
            />
            <Typography
              variant="body1"
              component="span"
              className="shop-setup-parent-label">
              Shop Settings
            </Typography>
            {showParentDot && <Box className="nav-dot" sx={{ ml: 'auto' }} />}
            <SetupChevron
              size={20}
              strokeWidth={1.75}
              color="#9A9A9A"
              style={{ marginLeft: showParentDot ? 8 : 'auto' }}
            />
          </ButtonBase>
          {setupExpanded && (
            <Box className="shop-setup-children">
              {visibleSetupChildren.map(child => (
                <Link
                  key={child.route}
                  to={child.route}
                  onClick={onNavigate}
                  className={`shop-setup-child${
                    path.includes(child.route) ? ' active' : ''
                  }`}>
                  {child.name}
                  {child.showDot && (
                    <Box className="nav-dot" sx={{ ml: 'auto' }} />
                  )}
                </Link>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <Drawer
        variant="permanent"
        className="desktop-side-nav"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}>
        <Toolbar />
        {renderContent()}
      </Drawer>
      <Drawer
        variant="temporary"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}>
        <Toolbar sx={{ marginTop: isBannerShown ? `${BANNER_HEIGHT}px` : 0 }} />
        {renderContent(closeMobileDrawer)}
      </Drawer>
    </>
  );
}

export default SideNav;
